import { useCallback, useEffect, useState } from "react";

export const INITIAL_DELAY_MS = 400;
export const PENALTY_SCORE_ANIMATION_DURATION_MS = 600;
export const CREDIT_SCORE_ANIMATION_DURATION_MS = 1600;

type AnimationPhase =
  | "idle"
  | "penalty-animating"
  | "penalty-applied"
  | "credit-animating"
  | "credit-applied";

interface UseCancellationScoreAnimationParams {
  currentScore: number;
  penalizedScore: number;
  finalScore: number;
  canUseCredit: boolean;
  /**
   * When true, automatically trigger the credit animation after the penalty
   * animation completes, without requiring a manual call to startCreditAnimation.
   * @default false
   */
  shouldAutoApplyCredit?: boolean;
}

interface UseCancellationScoreAnimationResult {
  /** endValue for ScoreCounter */
  displayScore: number;
  /** startValue for ScoreCounter */
  previousScore: number;
  /** Drives ScoreCounter.animationEnabled */
  isScoreAnimating: boolean;
  /** Color key: "initial" (primary), "penalized" (error), or "credit-applied" (primary) */
  scoreVariant: "initial" | "penalized" | "credit-applied";
  /** Whether the penalty line item should be visible */
  isPenaltyLineItemVisible: boolean;
  /** Whether the credit line item should be visible */
  isCreditLineItemVisible: boolean;
  /** Duration for the current score animation phase */
  scoreDuration: number;
  /** Whether all animations have finished and the UI is in a stable state */
  isAnimationComplete: boolean;
  /** Trigger credit animation (called when user presses "Preview my cancellation credit") */
  startCreditAnimation: () => void;
}

export function useCancellationScoreAnimation(
  params: UseCancellationScoreAnimationParams
): UseCancellationScoreAnimationResult {
  const {
    currentScore,
    penalizedScore,
    finalScore,
    canUseCredit,
    shouldAutoApplyCredit = false,
  } = params;

  const [phase, setPhase] = useState<AnimationPhase>("idle");

  // Reset phase when inputs change to prevent stale animation state
  useEffect(() => {
    setPhase("idle");
  }, [currentScore, penalizedScore]);

  // Penalty animation: auto-start on mount
  useEffect(() => {
    if (currentScore === penalizedScore) {
      return;
    }

    const timer1 = setTimeout(() => setPhase("penalty-animating"), INITIAL_DELAY_MS);
    const timer2 = setTimeout(
      () => setPhase("penalty-applied"),
      INITIAL_DELAY_MS + PENALTY_SCORE_ANIMATION_DURATION_MS
    );

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [currentScore, penalizedScore]);

  // Auto-trigger credit animation after penalty completes
  useEffect(() => {
    if (shouldAutoApplyCredit && phase === "penalty-applied" && canUseCredit) {
      setPhase("credit-animating");
    }
  }, [shouldAutoApplyCredit, phase, canUseCredit]);

  // Credit animation: complete after animation duration
  useEffect(() => {
    if (phase !== "credit-animating") {
      return;
    }

    const timer = setTimeout(() => setPhase("credit-applied"), CREDIT_SCORE_ANIMATION_DURATION_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  const startCreditAnimation = useCallback(() => {
    setPhase((current) => {
      if (current !== "penalty-applied" || !canUseCredit) {
        return current;
      }

      return "credit-animating";
    });
  }, [canUseCredit]);

  // When no score change, return fully static/complete state
  if (currentScore === penalizedScore) {
    return {
      displayScore: finalScore,
      previousScore: finalScore,
      isScoreAnimating: false,
      scoreVariant: getStaticScoreVariant({ canUseCredit, currentScore, penalizedScore }),
      isPenaltyLineItemVisible: currentScore !== penalizedScore,
      isCreditLineItemVisible: canUseCredit,
      scoreDuration: PENALTY_SCORE_ANIMATION_DURATION_MS,
      isAnimationComplete: true,
      startCreditAnimation,
    };
  }

  const { displayScore, previousScore } = getDisplayScores({
    phase,
    currentScore,
    penalizedScore,
    finalScore,
  });

  const isScoreAnimating = phase === "penalty-animating" || phase === "credit-animating";
  const isAnimationComplete =
    shouldAutoApplyCredit && canUseCredit
      ? phase === "credit-applied"
      : phase === "penalty-applied" || phase === "credit-applied";
  const scoreDuration =
    phase === "credit-animating" || phase === "credit-applied"
      ? CREDIT_SCORE_ANIMATION_DURATION_MS
      : PENALTY_SCORE_ANIMATION_DURATION_MS;

  return {
    displayScore,
    previousScore,
    isScoreAnimating,
    scoreDuration,
    scoreVariant: getScoreVariant(phase),
    isPenaltyLineItemVisible: phase !== "idle",
    isCreditLineItemVisible: phase === "credit-applied",
    isAnimationComplete,
    startCreditAnimation,
  };
}

function getStaticScoreVariant(params: {
  canUseCredit: boolean;
  currentScore: number;
  penalizedScore: number;
}): "initial" | "penalized" | "credit-applied" {
  if (params.canUseCredit) {
    return "credit-applied";
  }

  if (params.currentScore !== params.penalizedScore) {
    return "penalized";
  }

  return "initial";
}

function getScoreVariant(phase: AnimationPhase): "initial" | "penalized" | "credit-applied" {
  if (phase === "idle") {
    return "initial";
  }

  if (phase === "credit-animating" || phase === "credit-applied") {
    return "credit-applied";
  }

  return "penalized";
}

function getDisplayScores(params: {
  phase: AnimationPhase;
  currentScore: number;
  penalizedScore: number;
  finalScore: number;
}): { displayScore: number; previousScore: number } {
  const { phase, currentScore, penalizedScore, finalScore } = params;

  switch (phase) {
    case "idle": {
      return { displayScore: currentScore, previousScore: currentScore };
    }
    case "penalty-animating": {
      return { displayScore: penalizedScore, previousScore: currentScore };
    }
    case "penalty-applied": {
      return { displayScore: penalizedScore, previousScore: penalizedScore };
    }
    case "credit-animating": {
      return { displayScore: finalScore, previousScore: penalizedScore };
    }
    case "credit-applied": {
      return { displayScore: finalScore, previousScore: finalScore };
    }
  }
}
